/**
 * Copyright (c) 2018-present, Luojun Jin
 *
 * @emails john.king.ca@gmail.com
 */
import React from 'react';
import Select from 'react-select';
import Code from './Code';

export function toType(obj) {
  let type = getType(obj);
  // some extra disambiguation for numbers
  if (type === 'number') {
    if (isNaN(obj)) {
      type = 'nan';
    } else if ((obj | 0) != obj) {
      //bitwise OR produces integers
      type = 'float';
    } else {
      type = 'integer';
    }
  }
  return type;
}

//source: http://stackoverflow.com/questions/7390426/better-way-to-get-type-of-a-javascript-variable/7390612#7390612
function getType(obj) {
  return {}.toString
    .call(obj)
    .match(/\s([a-zA-Z]+)/)[1]
    .toLowerCase();
}

export function deepCopy(src, copy_namespace) {
  const type = toType(src);
  let result;
  let idx = copy_namespace.shift();
  if (type == 'array') {
    result = [...src];
  } else if (type == 'object') {
    result = {...src};
  }
  if (idx !== undefined) {
    result[idx] = this.deepCopy(src[idx], copy_namespace);
  }
  return result;
}

export const getNotes = (on_edit_enabled, on_add_enabled) => {

  let notes = [];
  if (on_edit_enabled) {
    notes.push(
      <span>
        To edit a value, try <Code>ctrl/cmd + click</Code> enter edit
        mode
      </span>
    );
    notes.push(
      <span>
        When editing a value, try <Code>ctrl/cmd + Enter</Code> to
        submit changes
      </span>
    );
    notes.push(
      <span>
        When editing a value, try <Code>Escape</Code> key to cancel
      </span>
    );
  }
  if (on_add_enabled) {
    notes.push(
      <span>
        When adding a new key, try <Code>Enter</Code> to submit
      </span>
    );
    notes.push(
      <span>
        When adding a new key, try <Code>Escape</Code> to cancel
      </span>
    );
  }

  if (notes.length === 0) {
    return null;
  }

  return (
    <div style={{ marginTop: '20px', fontStyle: 'italic' }}>
      Keyboard Shortcuts
      <ul>
        {notes.map(note => {
          return <li>{note}</li>;
        })}
      </ul>
    </div>
  );
};

export const getIconStyleInput = iconStyle => {
  return (
    <Select
      name="icon-style"
      value={iconStyle}
      options={[
        { value: 'circle', label: 'circle' },
        { value: 'square', label: 'square' },
        { value: 'triangle', label: 'triangle' },
      ]}
      onChange={val => {
        this.set('iconStyle', val);
      }}
    />
  );
};

export const getEditInput = onEdit => {
  return (
    <Select
      name="enable-edit"
      value={onEdit}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('onEdit', val);
      }}
    />
  );
};

export const getAddInput = onAdd => {
  return (
    <Select
      name="enable-add"
      value={onAdd}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('onAdd', val);
      }}
    />
  );
};

export const getDeleteInput = onDelete => {
  return (
    <Select
      name="enable-delete"
      value={onDelete}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('onDelete', val);
      }}
    />
  );
};

export const getEnableClipboardInput = enableClipboard => {
  return (
    <Select
      name="enable-clipboard"
      value={enableClipboard}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('enableClipboard', val);
      }}
    />
  );
};

export const getObjectSizeInput = displayObjectSize => {
  return (
    <Select
      name="display-object-size"
      value={displayObjectSize}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('displayObjectSize', val);
      }}
    />
  );
};

export const getDataTypesInput = displayDataTypes => {
  return (
    <Select
      name="display-data-types"
      value={displayDataTypes}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
      ]}
      onChange={val => {
        this.set('displayDataTypes', val);
      }}
    />
  );
};

export const getCollapsedStringsInput = collapseStringsAfter => {
  return (
    <Select
      name="collapse-strings"
      value={collapseStringsAfter}
      options={[
        { value: false, label: 'false' },
        { value: 5, label: 5 },
        { value: 10, label: 10 },
        { value: 15, label: 15 },
        { value: 20, label: 20 },
      ]}
      onChange={val => {
        this.set('collapseStringsAfter', val);
      }}
    />
  );
};

export const getCollapsedInput = ({collapsed, handleChange}) => {
  return (
    <Select
      name="collapsed"
      value={collapsed}
      options={[
        { value: true, label: 'true' },
        { value: false, label: 'false' },
        { value: 1, label: 1 },
        { value: 2, label: 2 },
      ]}
      onChange={val => {
        this.set('collapsed', val);
      }}
      // onChange={handleChange}
    />
  );
};

export const getIndentWidthInput = indentWidth => {
  return (
    <Select
      name="indent-width"
      value={indentWidth}
      options={[
        { value: 0, label: 0 },
        { value: 1, label: 1 },
        { value: 2, label: 2 },
        { value: 3, label: 3 },
        { value: 4, label: 4 },
        { value: 5, label: 5 },
        { value: 6, label: 6 },
        { value: 7, label: 7 },
        { value: 8, label: 8 },
        { value: 9, label: 9 },
        { value: 10, label: 10 },
      ]}
      onChange={val => {
        this.set('indentWidth', val);
      }}
    />
  );
};

export const getThemeInput = theme => {
  return (
    <Select
      name="theme-select"
      value={theme}
      options={[
        { value: 'apathy', label: 'apathy' },
        { value: 'apathy:inverted', label: 'apathy:inverted' },
        { value: 'ashes', label: 'ashes' },
        { value: 'bespin', label: 'bespin' },
        { value: 'brewer', label: 'brewer' },
        { value: 'bright:inverted', label: 'bright:inverted' },
        { value: 'bright', label: 'bright' },
        { value: 'chalk', label: 'chalk' },
        { value: 'codeschool', label: 'codeschool' },
        { value: 'colors', label: 'colors' },
        { value: 'eighties', label: 'eighties' },
        { value: 'embers', label: 'embers' },
        { value: 'flat', label: 'flat' },
        { value: 'google', label: 'google' },
        { value: 'grayscale', label: 'grayscale' },
        {
          value: 'grayscale:inverted',
          label: 'grayscale:inverted',
        },
        { value: 'greenscreen', label: 'greenscreen' },
        { value: 'harmonic', label: 'harmonic' },
        { value: 'hopscotch', label: 'hopscotch' },
        { value: 'isotope', label: 'isotope' },
        { value: 'marrakesh', label: 'marrakesh' },
        { value: 'mocha', label: 'mocha' },
        { value: 'monokai', label: 'monokai' },
        { value: 'ocean', label: 'ocean' },
        { value: 'paraiso', label: 'paraiso' },
        { value: 'pop', label: 'pop' },
        { value: 'railscasts', label: 'railscasts' },
        { value: 'rjv-default', label: 'rjv-default' },
        { value: 'shapeshifter', label: 'shapeshifter' },
        {
          value: 'shapeshifter:inverted',
          label: 'shapeshifter:inverted',
        },
        { value: 'solarized', label: 'solarized' },
        { value: 'summerfruit', label: 'summerfruit' },
        {
          value: 'summerfruit:inverted',
          label: 'summerfruit:inverted',
        },
        { value: 'threezerotwofour', label: 'threezerotwofour' },
        { value: 'tomorrow', label: 'tomorrow' },
        { value: 'tube', label: 'tube' },
        { value: 'twilight', label: 'twilight' },
      ]}
      onChange={val => {
        this.set('theme', val);
      }}
    />
  );
};